import {
  type Lead,
  type LeadActivity,
  LeadActivityType,
  type LeadNote,
} from '@up2stay/up2promo-api-client';
import { Avatar, AvatarFallback } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Card, CardContent } from '@up2stay/up2promo-ui';
import { Textarea } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import {
  Archive,
  ArchiveRestore,
  ArrowUp,
  CalendarDays,
  CircleDot,
  FileText,
  MessageSquare,
  Paperclip,
  Pencil,
  Phone,
  Plus,
  Share2,
  Tag,
  UserCircle2,
  UserPlus,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { formatRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import { LEAD_DETAIL_FLAT_CONTROL_CLASS } from '@/core/components/leads/lead-detail/lead-detail-field-styles';
import { LEAD_DETAIL_SECTION_TITLE_CLASS } from '@/core/components/leads/lead-detail/lead-detail-section-card';
import {
  getLeadActivityMetadata,
  isWorkspaceSharedActivityValue,
} from '@/core/lib/lead-activity-utils';
import { useIsMobile } from '@/core/hooks/use-mobile';

import { pickFieldActivityTemplate } from './lead-activity-field-template';
import {
  type LeadNoteActionLabels,
  LeadNoteActions,
} from './lead-note-actions';

const TIMELINE_INITIAL_LIMIT = 10;

type TimelineLabels = {
  title: string;
  empty: string;
  created: string;
  statusChanged: string;
  descriptionUpdated: string;
  fieldUpdated: string;
  fieldSet: string;
  fieldCleared: string;
  tagAdded: string;
  tagRemoved: string;
  eventAdded?: string;
  eventRemoved?: string;
  eventRemovedBecauseDeleted?: string;
  showMore: string;
  showLess: string;
  commentPlaceholder: string;
  fieldNames: Record<string, string>;
  assigneeChanged?: string;
  assigneeChangedAndShared?: string;
  nobody?: string;
  sharingChanged?: string;
  sharingVisible?: string;
  sharingHidden?: string;
  attachmentAdded?: string;
  attachmentRemoved?: string;
  contactedViaCall?: string;
  contactedViaEmail?: string;
  contactedViaSms?: string;
  contactedVia?: string;
  archived?: string;
  unarchived?: string;
  /** Resolve event id → display name for activity rows. */
  resolveEventName?: (
    eventId: string | null | undefined,
    storedName?: string | null,
  ) => string;
  /** Desktop composer submit button label. */
  commentSubmit: string;
  /** Name of the icon-only send button ("Notiz speichern", DEV-540). */
  commentSubmitAria: string;
};

type LeadDetailTimelineProps = {
  lead: Lead;
  locale: string;
  labels: TimelineLabels;
  statusLabels: Record<string, string>;
  resolveAuthorDisplayName: (userId: string | null | undefined) => string;
  resolveAuthorInitials: (userId: string | null | undefined) => string;
  isSubmittingComment: boolean;
  onSubmitComment: (content: string) => void;
  className?: string;
  /** When true, omit the “Aktivität” heading (tabs already label the surface). */
  hideTitle?: boolean;
  /** When true, omit the note composer (sticky page bar owns it on mobile). */
  hideComposer?: boolean;
  /** Viewer id: only their own notes get edit / delete (DEV-569). */
  currentUserId?: string | null;
  noteActionLabels?: LeadNoteActionLabels;
  /** Resolves true when saved; the editor stays open on failure. */
  onUpdateNote?: (noteId: string, content: string) => Promise<boolean>;
  /** Rejects on failure so the confirm dialog stays open. */
  onDeleteNote?: (noteId: string) => Promise<void>;
};

type TimelineEntry =
  | { kind: 'activity'; id: string; createdAt: string; activity: LeadActivity }
  | { kind: 'comment'; id: string; createdAt: string; note: LeadNote };

function activityIcon(type: LeadActivityType) {
  switch (type) {
    case LeadActivityType.CREATED:
      return UserPlus;
    case LeadActivityType.STATUS_CHANGED:
      return CircleDot;
    case LeadActivityType.DESCRIPTION_UPDATED:
      return FileText;
    case LeadActivityType.FIELD_UPDATED:
      return Pencil;
    case LeadActivityType.NOTE_ADDED:
      return MessageSquare;
    case LeadActivityType.ASSIGNEE_CHANGED:
      return UserCircle2;
    case LeadActivityType.WORKSPACE_SHARING_UPDATED:
      return Share2;
    case LeadActivityType.TAG_ADDED:
    case LeadActivityType.TAG_REMOVED:
      return Tag;
    case LeadActivityType.EVENT_ADDED:
    case LeadActivityType.EVENT_REMOVED:
      return CalendarDays;
    case LeadActivityType.ATTACHMENT_ADDED:
    case LeadActivityType.ATTACHMENT_REMOVED:
      return Paperclip;
    case LeadActivityType.CONTACTED_VIA:
      return Phone;
    case LeadActivityType.ARCHIVED:
      return Archive;
    case LeadActivityType.UNARCHIVED:
      return ArchiveRestore;
    default:
      return Plus;
  }
}

function formatActivityValue(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed || '—';
}

function interpolateBold(
  template: string,
  values: Record<string, string>,
): ReactNode {
  const parts = template.split(/(\{[a-zA-Z]+\})/g);
  return parts.map((part, index) => {
    const match = /^\{([a-zA-Z]+)\}$/.exec(part);
    if (match?.[1]) {
      const value = values[match[1]] ?? '—';
      return (
        <strong key={index} className="font-semibold text-foreground">
          {value}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

function formatActivityContent(
  entry: LeadActivity,
  labels: TimelineLabels,
  statusLabels: Record<string, string>,
  resolveUserLabel: (id: string | null | undefined) => string,
): ReactNode {
  const meta = getLeadActivityMetadata(entry);
  switch (entry.type) {
    case LeadActivityType.CREATED:
      return labels.created;
    case LeadActivityType.STATUS_CHANGED:
      return interpolateBold(labels.statusChanged, {
        from: statusLabels[meta.from ?? ''] ?? meta.from ?? '—',
        to: statusLabels[meta.to ?? ''] ?? meta.to ?? '—',
      });
    case LeadActivityType.DESCRIPTION_UPDATED:
      return labels.descriptionUpdated;
    case LeadActivityType.FIELD_UPDATED: {
      const fieldKey = meta.field ?? '';
      const fieldLabel = labels.fieldNames[fieldKey] ?? fieldKey;
      const template = pickFieldActivityTemplate(meta.from, meta.to, {
        updated: labels.fieldUpdated,
        set: labels.fieldSet,
        cleared: labels.fieldCleared,
      });
      return interpolateBold(template, {
        field: fieldLabel,
        from: formatActivityValue(meta.from),
        to: formatActivityValue(meta.to),
      });
    }
    case LeadActivityType.TAG_ADDED:
      return interpolateBold(labels.tagAdded, { tag: meta.tag ?? '' });
    case LeadActivityType.TAG_REMOVED:
      return interpolateBold(labels.tagRemoved, { tag: meta.tag ?? '' });
    case LeadActivityType.EVENT_ADDED:
      return interpolateBold(labels.eventAdded ?? 'added to event "{event}"', {
        event:
          labels.resolveEventName?.(meta.eventId, meta.eventName) ??
          meta.eventName ??
          meta.eventId ??
          '',
      });
    case LeadActivityType.EVENT_REMOVED:
      return interpolateBold(
        meta.reason === 'event_deleted'
          ? (labels.eventRemovedBecauseDeleted ??
              'removed from event "{event}" (event deleted)')
          : (labels.eventRemoved ?? 'removed from event "{event}"'),
        {
          event:
            labels.resolveEventName?.(meta.eventId, meta.eventName) ??
            meta.eventName ??
            meta.eventId ??
            '',
        },
      );
    case LeadActivityType.ASSIGNEE_CHANGED: {
      const fromLabel = meta.from?.trim()
        ? resolveUserLabel(meta.from)
        : (labels.nobody ?? '—');
      const toLabel = meta.to?.trim()
        ? resolveUserLabel(meta.to)
        : (labels.nobody ?? '—');
      const template =
        meta.sharedWithWorkspace === 'true'
          ? (labels.assigneeChangedAndShared ?? labels.assigneeChanged)
          : labels.assigneeChanged;
      return interpolateBold(template ?? 'Updated assignee', {
        from: fromLabel,
        to: toLabel,
      });
    }
    case LeadActivityType.WORKSPACE_SHARING_UPDATED:
      return interpolateBold(
        labels.sharingChanged ?? 'Updated workspace sharing',
        {
          from: isWorkspaceSharedActivityValue(meta.from)
            ? (labels.sharingVisible ?? 'Shared')
            : (labels.sharingHidden ?? 'Private'),
          to: isWorkspaceSharedActivityValue(meta.to)
            ? (labels.sharingVisible ?? 'Shared')
            : (labels.sharingHidden ?? 'Private'),
        },
      );
    case LeadActivityType.ATTACHMENT_ADDED:
      return interpolateBold(labels.attachmentAdded ?? 'Added attachment', {
        title: meta.title?.trim() || '—',
      });
    case LeadActivityType.ATTACHMENT_REMOVED:
      return interpolateBold(labels.attachmentRemoved ?? 'Removed attachment', {
        title: meta.title?.trim() || '—',
      });
    case LeadActivityType.CONTACTED_VIA: {
      const channel = meta.channel?.trim().toLowerCase();
      if (channel === 'call') {
        return labels.contactedViaCall ?? 'called';
      }
      if (channel === 'email') {
        return labels.contactedViaEmail ?? 'sent an email';
      }
      if (channel === 'sms') {
        return labels.contactedViaSms ?? 'sent an SMS';
      }
      return interpolateBold(labels.contactedVia ?? 'contacted via {channel}', {
        channel: channel || '—',
      });
    }
    case LeadActivityType.ARCHIVED:
      return labels.archived ?? 'archived this lead';
    case LeadActivityType.UNARCHIVED:
      return labels.unarchived ?? 'restored this lead';
    default:
      return entry.type;
  }
}

function buildTimelineEntries(lead: Lead): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  /** Rows need unique keys; API may omit ids so we must not collapse many rows into one. */
  let activityOrdinal = 0;
  for (const activity of lead.activity ?? []) {
    if (activity.type === LeadActivityType.NOTE_ADDED) continue;
    const stableActivityId =
      activity.id?.trim() ||
      `fallback-${activityOrdinal++}-${activity.createdAt}-${String(activity.type)}-${activity.actorUserId ?? ''}`;
    entries.push({
      kind: 'activity',
      id: `activity-${stableActivityId}`,
      createdAt: activity.createdAt,
      activity,
    });
  }

  let noteOrdinal = 0;
  for (const note of lead.notes ?? []) {
    const stableNoteId =
      note.id?.trim() ||
      `fallback-${noteOrdinal++}-${note.createdAt}-${note.authorUserId ?? ''}`;
    entries.push({
      kind: 'comment',
      id: `comment-${stableNoteId}`,
      createdAt: note.createdAt,
      note,
    });
  }

  if (entries.length === 0 && lead.createdAt) {
    entries.push({
      kind: 'activity',
      id: 'activity-legacy-created',
      createdAt: lead.createdAt,
      activity: {
        id: 'legacy-created',
        type: LeadActivityType.CREATED,
        actorUserId: lead.userId,
        createdAt: lead.createdAt,
      },
    });
  }

  return entries.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

/** Keep at least one line visible; empty + min-h-0 can clip placeholders (iOS/WebKit). */
const MIN_AUTO_TEXTAREA_HEIGHT_PX = 48;

function syncTextareaHeight(textarea: HTMLTextAreaElement | null) {
  if (!textarea) return;
  textarea.style.height = 'auto';
  const next = Math.max(textarea.scrollHeight, MIN_AUTO_TEXTAREA_HEIGHT_PX);
  textarea.style.height = `${next}px`;
}

interface TimelineTimeProps {
  time: string;
  showBelow: boolean;
  visible: boolean;
}

function TimelineTime({ time, showBelow, visible }: TimelineTimeProps) {
  if (!visible || !time) return null;

  if (showBelow) {
    return (
      <p className="mt-1 text-xs leading-tight text-muted-foreground/70">
        {time}
      </p>
    );
  }

  return <span className="text-muted-foreground/70"> · {time}</span>;
}

export function LeadDetailTimeline({
  lead,
  locale,
  labels,
  statusLabels,
  resolveAuthorDisplayName,
  resolveAuthorInitials,
  isSubmittingComment,
  onSubmitComment,
  className,
  hideTitle = false,
  hideComposer = false,
  currentUserId,
  noteActionLabels,
  onUpdateNote,
  onDeleteNote,
}: LeadDetailTimelineProps) {
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const saveEdit = async (noteId: string) => {
    const content = editDraft.trim();
    if (!content || !onUpdateNote) return;
    setIsSavingEdit(true);
    const saved = await onUpdateNote(noteId, content);
    setIsSavingEdit(false);
    if (saved) setEditingNoteId(null);
  };
  const isMobile = useIsMobile();
  const entries = useMemo(() => buildTimelineEntries(lead), [lead]);
  const [olderBatchesLoadedRaw, setOlderBatchesLoaded] = useState(0);
  const [expandedTimeId, setExpandedTimeId] = useState<string | null>(null);
  const [prevLeadId, setPrevLeadId] = useState(lead.id);
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  if (lead.id !== prevLeadId) {
    setPrevLeadId(lead.id);
    setOlderBatchesLoaded(0);
    setExpandedTimeId(null);
  }

  const maxBatches = useMemo(
    () =>
      Math.max(
        0,
        Math.ceil(
          Math.max(0, entries.length - TIMELINE_INITIAL_LIMIT) /
            TIMELINE_INITIAL_LIMIT,
        ),
      ),
    [entries.length],
  );
  const olderBatchesLoaded = Math.min(olderBatchesLoadedRaw, maxBatches);

  const oldestEntryId = entries[0]?.id;

  useEffect(() => {
    syncTextareaHeight(textareaRef.current);
  }, [draft]);

  const visibleCount = Math.min(
    TIMELINE_INITIAL_LIMIT + olderBatchesLoaded * TIMELINE_INITIAL_LIMIT,
    entries.length,
  );

  const visibleEntries = useMemo(() => {
    if (entries.length <= visibleCount) return entries;
    return entries.slice(-visibleCount);
  }, [entries, visibleCount]);

  const remainingOlderCount = Math.max(0, entries.length - visibleCount);
  const canLoadOlder = remainingOlderCount > 0;
  const canShowRecentOnly = olderBatchesLoaded > 0;

  const loadOlderEntries = () => {
    setOlderBatchesLoaded((batches) => batches + 1);
  };

  const showRecentOnly = () => {
    setOlderBatchesLoaded(0);
    setExpandedTimeId(null);
  };

  const handleSubmit = () => {
    const trimmed = draft.trim();
    if (!trimmed || isSubmittingComment) return;
    onSubmitComment(trimmed);
    setDraft('');
  };

  const toggleTime = (entryId: string) => {
    setExpandedTimeId((current) => (current === entryId ? null : entryId));
  };

  const isTimeVisible = (entryId: string) => {
    if (!isMobile) return true;
    if (entryId === oldestEntryId) return true;
    return expandedTimeId === entryId;
  };

  const composer = (
    <div
      className={cn(
        'flex flex-col gap-2.5 rounded-[12px] border border-[color:var(--surface-border)] bg-[color:var(--surface-2)] px-3.5 py-3',
        isMobile && 'bg-muted/20',
      )}
    >
      <Textarea
        ref={textareaRef}
        value={draft}
        rows={1}
        onChange={(e) => {
          setDraft(e.target.value);
          syncTextareaHeight(e.target);
        }}
        placeholder={labels.commentPlaceholder}
        disabled={isSubmittingComment}
        className={cn(
          LEAD_DETAIL_FLAT_CONTROL_CLASS,
          'min-h-[40px] w-full resize-none overflow-hidden px-0 py-0 text-[13px] leading-relaxed text-muted-foreground',
        )}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />
      <div className="flex items-center justify-end gap-2">
        {isMobile ? (
          <Button
            type="button"
            size="icon"
            className="h-8 w-8 shrink-0 rounded-full"
            disabled={!draft.trim() || isSubmittingComment}
            onClick={handleSubmit}
            aria-label={labels.commentSubmitAria}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className="h-[30px] rounded-[8px] px-3 text-[12.5px] font-semibold"
            disabled={!draft.trim() || isSubmittingComment}
            onClick={handleSubmit}
          >
            {labels.commentSubmit}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      {!hideTitle ? (
        <h2 className={LEAD_DETAIL_SECTION_TITLE_CLASS}>{labels.title}</h2>
      ) : null}

      {!isMobile && !hideComposer ? composer : null}

      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">{labels.empty}</p>
      ) : (
        <>
          {canLoadOlder || canShowRecentOnly ? (
            <div className="flex min-w-0 gap-2.5">
              <div className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                {canLoadOlder ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-auto justify-start px-2.5 text-xs text-muted-foreground hover:text-foreground"
                    onClick={loadOlderEntries}
                  >
                    {`${labels.showMore}${remainingOlderCount > 0 ? ` (${remainingOlderCount})` : ''}`}
                  </Button>
                ) : null}
                {canShowRecentOnly ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-auto justify-start px-2.5 text-xs text-muted-foreground hover:text-foreground"
                    onClick={showRecentOnly}
                  >
                    {labels.showLess}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          <ul>
            {visibleEntries.map((entry, index) => {
              const isLast = index === visibleEntries.length - 1;
              const time = formatRelativeTime(entry.createdAt, locale);
              const showTimeBelow = isMobile;
              const showTime = isTimeVisible(entry.id);

              const handleRowClick = () => {
                if (!isMobile || entry.id === oldestEntryId) return;
                toggleTime(entry.id);
              };

              if (entry.kind === 'comment') {
                const authorId = entry.note.authorUserId;
                const noteId = entry.note.id;
                const canManageNote =
                  !!noteId &&
                  !!currentUserId &&
                  authorId === currentUserId &&
                  !!noteActionLabels &&
                  !!onUpdateNote &&
                  !!onDeleteNote;
                const isEditing = canManageNote && editingNoteId === noteId;
                const rowTappable =
                  isMobile && entry.id !== oldestEntryId && !isEditing;
                const authorName = resolveAuthorDisplayName(authorId);
                const authorInitials = resolveAuthorInitials(authorId);

                return (
                  <li
                    key={entry.id}
                    className={cn(
                      'relative flex min-w-0 gap-2.5',
                      !isLast && 'pb-4',
                    )}
                  >
                    <div className="relative z-[1] flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                      <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    {!isLast ? (
                      <div
                        className="absolute left-[7px] top-[calc(0.875rem+4px)] bottom-[4px] w-px -translate-x-1/2 bg-border/80"
                        aria-hidden
                      />
                    ) : null}
                    <div
                      className={cn(
                        'min-w-0 flex-1',
                        rowTappable && 'cursor-pointer',
                      )}
                      onClick={rowTappable ? handleRowClick : undefined}
                      onKeyDown={
                        rowTappable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRowClick();
                              }
                            }
                          : undefined
                      }
                      role={rowTappable ? 'button' : undefined}
                      tabIndex={rowTappable ? 0 : undefined}
                    >
                      <Card className="min-w-0 overflow-hidden shadow-none">
                        <CardContent className="flex gap-2.5 p-3 sm:p-3.5">
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarFallback className="bg-[color:var(--ghost-accent-bg)] text-xs text-[color:var(--ghost-accent-fg)]">
                              {authorInitials}
                            </AvatarFallback>
                          </Avatar>
                          <div
                            className={cn(
                              'min-w-0 flex-1',
                              canManageNote && !isEditing && 'pr-7',
                            )}
                          >
                            <p className="text-xs font-medium leading-tight text-foreground">
                              {authorName}
                              {entry.note.updatedAt && noteActionLabels ? (
                                <span className="ml-1.5 font-normal text-muted-foreground">
                                  ({noteActionLabels.edited})
                                </span>
                              ) : null}
                              {!showTimeBelow && showTime ? (
                                <TimelineTime
                                  time={time}
                                  showBelow={false}
                                  visible
                                />
                              ) : null}
                            </p>
                            {isEditing && noteActionLabels ? (
                              <div className="mt-1.5 flex flex-col gap-2">
                                <Textarea
                                  value={editDraft}
                                  onChange={(e) => setEditDraft(e.target.value)}
                                  rows={3}
                                  autoFocus
                                  aria-label={noteActionLabels.edit}
                                  className="min-h-[72px] text-sm"
                                />
                                <div className="flex justify-end gap-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingNoteId(null)}
                                    disabled={isSavingEdit}
                                  >
                                    {noteActionLabels.cancel}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => void saveEdit(noteId)}
                                    disabled={isSavingEdit || !editDraft.trim()}
                                  >
                                    {noteActionLabels.save}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <p className="mt-1 whitespace-pre-wrap text-sm leading-snug text-foreground">
                                {entry.note.content}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                      {showTimeBelow ? (
                        <TimelineTime
                          time={time}
                          showBelow
                          visible={showTime}
                        />
                      ) : null}
                    </div>
                    {canManageNote && !isEditing ? (
                      <div className="absolute right-1.5 top-1.5">
                        <LeadNoteActions
                          labels={noteActionLabels}
                          onEdit={() => {
                            setEditDraft(entry.note.content);
                            setEditingNoteId(noteId);
                          }}
                          onDelete={() => onDeleteNote(noteId)}
                        />
                      </div>
                    ) : null}
                  </li>
                );
              }

              const Icon = activityIcon(entry.activity.type);
              const content = formatActivityContent(
                entry.activity,
                labels,
                statusLabels,
                resolveAuthorDisplayName,
              );
              const actorName = resolveAuthorDisplayName(
                entry.activity.actorUserId,
              );

              return (
                <li
                  key={entry.id}
                  className={cn(
                    'relative flex min-w-0 gap-2.5 text-xs leading-tight',
                    !isLast && 'pb-4',
                  )}
                >
                  <div className="relative z-[1] flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  {!isLast ? (
                    <div
                      className="absolute left-[7px] top-[calc(0.875rem+4px)] bottom-[4px] w-px -translate-x-1/2 bg-border/80"
                      aria-hidden
                    />
                  ) : null}
                  <div
                    className={cn(
                      'min-w-0 flex-1 self-center',
                      isMobile &&
                        entry.id !== oldestEntryId &&
                        'cursor-pointer',
                    )}
                    onClick={handleRowClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick();
                      }
                    }}
                    role={
                      isMobile && entry.id !== oldestEntryId
                        ? 'button'
                        : undefined
                    }
                    tabIndex={
                      isMobile && entry.id !== oldestEntryId ? 0 : undefined
                    }
                  >
                    <p className="min-w-0 text-muted-foreground">
                      <span className="font-medium text-foreground/90">
                        {actorName}
                      </span>{' '}
                      <span>{content}</span>
                      {!showTimeBelow && showTime ? (
                        <TimelineTime time={time} showBelow={false} visible />
                      ) : null}
                    </p>
                    {showTimeBelow ? (
                      <TimelineTime time={time} showBelow visible={showTime} />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {!hideComposer && isMobile ? composer : null}
    </section>
  );
}
